import {DetailsField, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {useTranslation} from '@/translations/i18n'
import {Text} from '@/components/ui/text/Text'
import {Trans} from 'react-i18next'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {useOutletContext} from 'react-router-dom'
import {ProjectExtended} from '../../types'
import {useGetAbsences} from '@/features/accesses/queries/useGetAbsences'
import {useAbsencesCalculations} from '../../hooks/useAbsencesCalculations'
import {useGetPlannedAccessesDetails} from '@/features/planned-accesses/queries/useGetPlannedAccessesDetails'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {CheckCircleIcon, AlertCircleIcon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'

export const RecordedAbsencesSection = () => {
    const {t} = useTranslation()
    const {project} = useOutletContext<{project: ProjectExtended}>()

    const {data: absences, isLoading: isLoadingAbsences} = useGetAbsences({
        params: {
            projectId: project.id,
            page: 1,
            limit: 99999
        }
    })

    const {data: plannedAccesses, isLoading: isLoadingPlannedAccesses} = useGetPlannedAccessesDetails({
        urlParams: {
            projectId: project.id
        }
    })

    const isLoading = isLoadingAbsences || isLoadingPlannedAccesses

    const {totalAbsencesPaid, percentage, maxAbsencePercentage, totalAbsencesAllowed} = useAbsencesCalculations({
        project,
        absences,
        plannedAccesses
    })

    const {palette} = useTheme()

    const renderSkeletonContent = () => (
        <Grid columns={2} gap={3} fullWidth>
            {Array.from({length: 4}).map((_, index) => (
                <DetailsField key={index} fullWidth>
                    <Skeleton width={120} height={18} />
                    <Skeleton width={80} height={20} />
                </DetailsField>
            ))}
        </Grid>
    )

    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t('projects:recorded_absences')}
            headingEndSlot={
                <Text palette="neutral" shade="500" size="textSm">
                    <Trans
                        i18nKey="commons:total_score_x_bolded"
                        components={{
                            b: <Text render="span" palette="neutral" shade="500" size="textSm" weight="500" />
                        }}
                        values={{count: absences?.length || 0}}
                    />
                </Text>
            }
        >
            {isLoading ? (
                renderSkeletonContent()
            ) : (
                <Grid columns={2} gap={3} fullWidth>
                    <DetailsField fullWidth>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('projects:total_absences')}
                        </Text>
                        <Text palette="neutral" shade="950" size="textSm" weight="500">
                            {totalAbsencesPaid || '-'}
                        </Text>
                    </DetailsField>
                    <DetailsField fullWidth>
                        <Flexbox gap={1} align="center">
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('projects:absence_threshold')}
                            </Text>
                            {percentage < maxAbsencePercentage ? (
                                <CheckCircleIcon size={16} color={palette.success[600]} />
                            ) : (
                                <AlertCircleIcon size={16} color={palette.orange[600]} />
                            )}
                        </Flexbox>

                        <Text palette="neutral" shade="950" size="textSm" weight="500">
                            {percentage ? `${percentage}%` : '-'}
                        </Text>
                    </DetailsField>
                    <DetailsField fullWidth>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('projects:paid_absences')}
                        </Text>
                        <Text palette="neutral" shade="500" size="textSm" weight="500">
                            {totalAbsencesPaid && absences?.length ? (
                                <Trans
                                    i18nKey="projects:count_of_total"
                                    components={{
                                        b: (
                                            <Text
                                                render="span"
                                                palette="neutral"
                                                shade="950"
                                                size="textSm"
                                                weight="500"
                                            />
                                        )
                                    }}
                                    values={{count: totalAbsencesPaid, total: absences?.length || 0}}
                                />
                            ) : (
                                '-'
                            )}
                        </Text>
                    </DetailsField>
                    <DetailsField fullWidth>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('projects:allowed_by_project')}
                        </Text>
                        <Flexbox gap={1} align="center">
                            <Text palette="neutral" shade="950" size="textSm" weight="500">
                                {totalAbsencesAllowed ? totalAbsencesAllowed : ''}
                            </Text>
                            <Text palette="neutral" shade="500" size="textSm" weight="500">
                                {`(${maxAbsencePercentage}%)`}
                            </Text>
                        </Flexbox>
                    </DetailsField>
                </Grid>
            )}
        </DetailsFieldsSection>
    )
}
